import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import axios from "axios";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { MLAnalysisPanel } from "../components/MLAnalysisPanel";

// Import optimized interview components
import {
  WPMMeter,
  InterviewTimer,
  PastInterviews,
  BenchmarkPanel,
  ResumableInterviewsCard,
  InterviewFeedbackView,
  InterviewHistoryDetail,
  WPM_CONFIG,
  formatTime,
  getScoreColor,
  getScoreBgColor,
  getWPMStatus,
  getWPMMeterValue,
  useThrottledCallback
} from "../components/interview";

import { 
  Mic, MicOff, Send, TrendingUp, Target, Award, Clock, CheckCircle, AlertCircle, 
  Eye, X, Play, Pause, Video, VideoOff, Users, Zap, DollarSign, Code,
  BarChart3, Brain, Sparkles, Timer, Volume2, Camera, RefreshCw, ChevronRight,
  MessageSquare, ThumbsUp, ThumbsDown, Lightbulb, TrendingDown, Minus, Gauge, Activity,
  Circle, Square, Download, Film, Star, PlayCircle, StopCircle, User,
  FileText, Upload, FileCheck, Loader2, Wand2, AlertCircle as AlertCircleIcon
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const AIInterviewBuddy = () => {
  // Stage management
  const [stage, setStage] = useState("setup"); // setup, interview, feedback, history, history-detail
  
  // Configuration state
  const [config, setConfig] = useState(null);
  const [selectedMode, setSelectedMode] = useState("standard");
  const [careerField, setCareerField] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [companyCulture, setCompanyCulture] = useState("corporate");
  const [industry, setIndustry] = useState("technology");
  const [experienceLevel, setExperienceLevel] = useState("mid");
  const [stressLevel, setStressLevel] = useState([3]);
  const [panelSize, setPanelSize] = useState([3]);
  const [adaptiveDifficulty, setAdaptiveDifficulty] = useState(true);
  
  // Resume-Based Interview State
  const [useResume, setUseResume] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [parsedResume, setParsedResume] = useState(null);
  const [savedResume, setSavedResume] = useState(null);
  const [resumeParsing, setResumeParsing] = useState(false);
  const [useAIParsing, setUseAIParsing] = useState(true);
  const resumeInputRef = useRef(null);
  
  // Session state
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentInterviewer, setCurrentInterviewer] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Pause & Resume state
  const [resumableSessions, setResumableSessions] = useState([]);
  const [pausing, setPausing] = useState(false);
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerRef = useRef(null);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceMetrics, setVoiceMetrics] = useState(null);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);
  const startTimeRef = useRef(null);
  const finalTranscriptRef = useRef("");
  
  // Real-time WPM tracking
  const [liveWPM, setLiveWPM] = useState(0);
  const [wpmHistory, setWpmHistory] = useState([]);
  const [timeAboveOptimal, setTimeAboveOptimal] = useState(0);
  const [timeBelowOptimal, setTimeBelowOptimal] = useState(0);
  const [timeInOptimal, setTimeInOptimal] = useState(0);
  const wordCountRef = useRef(0);
  const wpmIntervalRef = useRef(null);
  const lastWordCountRef = useRef(0);
  const lastCheckTimeRef = useRef(Date.now());
  
  // Video state
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [bodyLanguageAssessment, setBodyLanguageAssessment] = useState({
    eye_contact: 5,
    posture: 5,
    facial_expressions: 5,
    hand_gestures: 5,
    overall_presence: 5
  });
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  
  // Feedback modal
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  
  // Benchmarks
  const [benchmarks, setBenchmarks] = useState([]);
  
  // Session Recording state
  const [isSessionRecording, setIsSessionRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [sessionRecordingUrl, setSessionRecordingUrl] = useState(null);
  const [showRecordingPreview, setShowRecordingPreview] = useState(false);
  const [recordingHighlights, setRecordingHighlights] = useState([]);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const combinedStreamRef = useRef(null);
  
  // ML Analysis state
  const [bodyLanguageAnalysis, setBodyLanguageAnalysis] = useState(null);
  const [facialExpressions, setFacialExpressions] = useState(null);
  const [mlAnalysisEnabled, setMlAnalysisEnabled] = useState(true);
  
  const token = localStorage.getItem("token");
  
  // Fetch configuration on mount
  useEffect(() => {
    fetchConfig();
    fetchHistory();
    fetchSavedResume();
    fetchResumableSessions();
  }, []);
  
  // Fetch resumable (paused / in-progress) sessions
  const fetchResumableSessions = async () => {
    try {
      const res = await axios.get(`${API}/interview-buddy/resumable-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResumableSessions(res.data.sessions || []);
    } catch (error) {
      console.error("Error fetching resumable sessions:", error);
    }
  };
  
  // Pause the current interview - saves progress so user can resume later
  const handlePauseInterview = async () => {
    if (!session?.id) return;
    setPausing(true);
    try {
      const res = await axios.post(
        `${API}/interview-buddy/pause/${session.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Interview saved! You can resume anytime.");
      // Stop any running timers/recordings
      setIsTimerRunning(false);
      if (isRecording && recognitionRef.current) {
        recognitionRef.current.stop();
      }
      // Reset session and go back to setup
      setSession(null);
      setCurrentQuestion(null);
      setAnswer("");
      setStage("setup");
      // Refresh resumable list
      fetchResumableSessions();
    } catch (error) {
      console.error("Pause error:", error);
      toast.error(error.response?.data?.detail || "Failed to save interview");
    } finally {
      setPausing(false);
    }
  };
  
  // Resume a paused interview - jump straight into the interview view
  const handleResumeInterview = async (sessionId) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/interview-buddy/resume/${sessionId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSession(res.data.session);
      setCurrentQuestion(res.data.current_question);
      setCurrentInterviewer(res.data.current_interviewer);
      setAnswer("");
      setStage("interview");
      // Start timer if applicable
      if (res.data.session.time_limit_per_question > 0) {
        setTimeRemaining(res.data.session.time_limit_per_question);
        setIsTimerRunning(true);
      }
      toast.success(res.data.message || "Interview resumed!");
    } catch (error) {
      console.error("Resume error:", error);
      toast.error(error.response?.data?.detail || "Failed to resume interview");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch saved resume
  const fetchSavedResume = async () => {
    try {
      const res = await axios.get(`${API}/interview-buddy/saved-resume`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.has_resume) {
        setSavedResume(res.data);
        setParsedResume(res.data.data);
      }
    } catch (error) {
      console.log("No saved resume found");
    }
  };
  
  // Handle resume file selection
  const handleResumeFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['.pdf', '.docx', '.txt'];
      const fileName = file.name.toLowerCase();
      
      if (!allowedTypes.some(ext => fileName.endsWith(ext))) {
        toast.error("Please upload a PDF, DOCX, or TXT file");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 5MB");
        return;
      }
      
      setResumeFile(file);
      setParsedResume(null);
    }
  };
  
  // Parse resume
  const parseResume = async () => {
    if (!resumeFile) {
      toast.error("Please select a resume file first");
      return;
    }
    
    setResumeParsing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', resumeFile);
      formData.append('use_ai', useAIParsing);
      
      const res = await axios.post(`${API}/interview-buddy/parse-resume`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        setParsedResume(res.data.data);
        setSavedResume({ data: res.data.data, filename: resumeFile.name });
        toast.success(`Resume parsed successfully using ${res.data.parsing_method === 'ai' ? 'AI' : 'simple'} extraction!`);
        
        // Auto-fill target role if found
        if (res.data.data.experiences && res.data.data.experiences.length > 0) {
          const latestRole = res.data.data.experiences[0].role || res.data.data.experiences[0].title;
          if (latestRole && !targetRole) {
            setTargetRole(latestRole);
          }
        }
      }
    } catch (error) {
      console.error("Resume parsing error:", error);
      toast.error(error.response?.data?.detail || "Failed to parse resume");
    } finally {
      setResumeParsing(false);
    }
  };
  
  // Start resume-based interview
  const startResumeInterview = async () => {
    if (!parsedResume) {
      toast.error("Please parse your resume first");
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('mode', selectedMode);
      formData.append('target_role', targetRole || 'Professional');
      formData.append('experience_level', experienceLevel);
      formData.append('company_culture', companyCulture);
      formData.append('industry', industry);
      formData.append('use_saved_resume', 'true');
      
      const res = await axios.post(`${API}/interview-buddy/start-resume-interview`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSession(res.data.session);
      setCurrentQuestion(res.data.current_question);
      setStage("interview");
      
      // Set timer for stress mode
      if (res.data.session.time_limit_per_question > 0) {
        setTimeRemaining(res.data.session.time_limit_per_question);
        setIsTimerRunning(true);
      }
      
      toast.success("Resume-based interview started! Questions are personalized to your experience.");
    } catch (error) {
      console.error("Start interview error:", error);
      toast.error(error.response?.data?.detail || "Failed to start interview");
    } finally {
      setLoading(false);
    }
  };
  
  // Timer effect
  useEffect(() => {
    if (isTimerRunning && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setIsTimerRunning(false);
            toast.warning("Time's up! Please submit your answer.");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isTimerRunning, timeRemaining]);
  
  // Real-time WPM calculation effect
  useEffect(() => {
    if (isRecording) {
      wpmIntervalRef.current = setInterval(() => {
        const currentTime = Date.now();
        const elapsedSinceLastCheck = (currentTime - lastCheckTimeRef.current) / 1000; // in seconds
        const currentWordCount = answer.split(/\s+/).filter(w => w.length > 0).length;
        const newWords = currentWordCount - lastWordCountRef.current;
        
        // Calculate WPM based on recent speaking rate
        if (elapsedSinceLastCheck > 0 && newWords >= 0) {
          const instantWPM = Math.round((newWords / elapsedSinceLastCheck) * 60);
          
          // Smooth the WPM with weighted average
          setLiveWPM(prev => {
            const smoothedWPM = prev === 0 ? instantWPM : Math.round(prev * 0.6 + instantWPM * 0.4);
            return Math.min(smoothedWPM, 250); // Cap at 250 WPM
          });
          
          // Track WPM history
          if (instantWPM > 0) {
            setWpmHistory(prev => [...prev.slice(-59), instantWPM]); // Keep last 60 readings
          }
          
          // Track time in different zones
          if (instantWPM > 0) {
            if (instantWPM < WPM_CONFIG.minOptimal) {
              setTimeBelowOptimal(prev => prev + 1);
            } else if (instantWPM > WPM_CONFIG.maxOptimal) {
              setTimeAboveOptimal(prev => prev + 1);
            } else {
              setTimeInOptimal(prev => prev + 1);
            }
          }
        }
        
        lastWordCountRef.current = currentWordCount;
        lastCheckTimeRef.current = currentTime;
      }, 1000); // Update every second
    }
    
    return () => {
      if (wpmIntervalRef.current) {
        clearInterval(wpmIntervalRef.current);
      }
    };
  }, [isRecording, answer]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (wpmIntervalRef.current) {
        clearInterval(wpmIntervalRef.current);
      }
    };
  }, []);
  
  // Handle video srcObject when video element and stream are both ready
  useEffect(() => {
    if (isVideoEnabled && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.onloadedmetadata = () => {
        if (videoRef.current) {
          videoRef.current.play().catch(e => {
            console.error("Video play error:", e);
          });
        }
      };
    }
  }, [isVideoEnabled]);
  
  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/interview-buddy/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Failed to load interview configuration");
    }
  };
  
  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/interview-buddy/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.sessions || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };
  
  const fetchBenchmarks = async () => {
    try {
      const response = await axios.get(`${API}/interview-buddy/benchmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBenchmarks(response.data.benchmarks || []);
    } catch (error) {
      console.error("Error fetching benchmarks:", error);
    }
  };
  
  // Improved Voice Recognition Setup
  const initSpeechRecognition = useCallback(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error("Speech recognition not supported in this browser. Please use Chrome or Edge.");
      return null;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    // Optimized configuration for real-time recognition
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;
    
    // Track processed result indices to prevent duplicate processing
    let lastProcessedIndex = 0;
    
    recognition.onresult = (event) => {
      let interimText = '';
      let newFinalText = '';
      
      // Process all results from the last processed index
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        
        if (result.isFinal) {
          // This is final text - format and append
          let formattedText = text.trim();
          
          // Capitalize first letter
          if (formattedText.length > 0) {
            formattedText = formattedText.charAt(0).toUpperCase() + formattedText.slice(1);
          }
          
          // Add period if no ending punctuation
          if (formattedText.length > 0 && !/[.!?]$/.test(formattedText)) {
            formattedText += '.';
          }
          
          newFinalText += formattedText + ' ';
          lastProcessedIndex = i + 1;
        } else {
          // Interim text - show immediately for real-time feedback
          interimText += text;
        }
      }
      
      // CRITICAL: Append new final text to existing content (NEVER replace)
      if (newFinalText.trim()) {
        const currentText = finalTranscriptRef.current || '';
        const trimmedCurrent = currentText.trim();
        const trimmedNew = newFinalText.trim();
        
        // Append with proper spacing
        if (trimmedCurrent && trimmedNew) {
          // Add space or newline between existing and new text
          finalTranscriptRef.current = trimmedCurrent + ' ' + trimmedNew + ' ';
        } else if (trimmedNew) {
          finalTranscriptRef.current = trimmedNew + ' ';
        }
        
        // Update state with accumulated text
        setAnswer(finalTranscriptRef.current);
        setTranscript(finalTranscriptRef.current);
        
        console.log('[Speech] Final appended:', trimmedNew);
        console.log('[Speech] Total accumulated:', finalTranscriptRef.current);
      }
      
      // Show interim results immediately for real-time feedback
      if (interimText) {
        // Display: accumulated final text + current interim text (grayed/italic in UI)
        setTranscript(finalTranscriptRef.current + interimText);
      }
    };
    
    recognition.onerror = (event) => {
      console.error("[Speech] Recognition error:", event.error);
      if (event.error === 'no-speech' || event.error === 'aborted') {
        return; // Ignore these non-critical errors
      }
      toast.error(`Speech recognition error: ${event.error}`);
    };
    
    recognition.onend = () => {
      // Auto-restart if still recording (browser may auto-stop after ~60s)
      if (isRecording && recognitionRef.current) {
        console.log('[Speech] Recognition ended, restarting...');
        try {
          setTimeout(() => {
            if (isRecording && recognitionRef.current) {
              recognition.start();
            }
          }, 100);
        } catch (e) {
          console.log("[Speech] Restart error:", e);
        }
      }
    };
    
    recognition.onstart = () => {
      console.log('[Speech] Recognition started');
    };
    
    return recognition;
  }, [isRecording]);
  
  const startRecording = () => {
    const recognition = initSpeechRecognition();
    if (!recognition) return;
    
    recognitionRef.current = recognition;
    startTimeRef.current = Date.now();
    
    // CRITICAL: Preserve existing answer text when starting a new recording session
    // This ensures multiple recording sessions append to the same answer
    const existingAnswer = answer.trim();
    if (existingAnswer) {
      finalTranscriptRef.current = existingAnswer + ' ';
      console.log('[Speech] Preserving existing answer:', existingAnswer);
    } else {
      finalTranscriptRef.current = '';
    }
    
    lastWordCountRef.current = existingAnswer.split(/\s+/).filter(w => w.length > 0).length;
    lastCheckTimeRef.current = Date.now();
    
    // Reset WPM tracking for this session (but don't reset the answer text!)
    setLiveWPM(0);
    setWpmHistory([]);
    setTimeAboveOptimal(0);
    setTimeBelowOptimal(0);
    setTimeInOptimal(0);
    
    setIsRecording(true);
    
    try {
      recognition.start();
      toast.success("🎤 Voice recording started. Speak clearly into your microphone.");
    } catch (error) {
      console.error("Error starting recognition:", error);
      toast.error("Failed to start voice recording. Please try again.");
      setIsRecording(false);
    }
  };
  
  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    
    const duration = (Date.now() - startTimeRef.current) / 1000;
    const wordCount = answer.split(/\s+/).filter(w => w.length > 0).length;
    
    // Calculate final voice metrics
    const avgWPM = wpmHistory.length > 0 
      ? Math.round(wpmHistory.reduce((a, b) => a + b, 0) / wpmHistory.length)
      : Math.round((wordCount / duration) * 60);
    
    // Analyze filler words
    const fillerWords = ["um", "uh", "er", "ah", "like", "you know", "basically", "actually", "literally"];
    const answerLower = answer.toLowerCase();
    let fillerCount = 0;
    const fillerDetected = [];
    fillerWords.forEach(filler => {
      const regex = new RegExp(`\\b${filler}\\b`, 'gi');
      const matches = answerLower.match(regex);
      if (matches) {
        fillerCount += matches.length;
        fillerDetected.push(...matches);
      }
    });
    
    // Calculate clarity score based on filler words
    const fillerPercentage = wordCount > 0 ? (fillerCount / wordCount) * 100 : 0;
    const clarityScore = Math.max(0, Math.min(100, Math.round(100 - fillerPercentage * 10)));
    
    // Calculate pace assessment
    let paceAssessment = "optimal";
    let paceRecommendation = "Great speaking pace!";
    if (avgWPM < WPM_CONFIG.tooSlow) {
      paceAssessment = "too_slow";
      paceRecommendation = "Try to speak a bit faster to maintain engagement. Aim for 120-150 WPM.";
    } else if (avgWPM < WPM_CONFIG.minOptimal) {
      paceAssessment = "slightly_slow";
      paceRecommendation = "Good pace, but could be slightly more energetic.";
    } else if (avgWPM > WPM_CONFIG.tooFast) {
      paceAssessment = "too_fast";
      paceRecommendation = "Speaking too quickly. Slow down and pause for emphasis.";
    } else if (avgWPM > WPM_CONFIG.maxOptimal) {
      paceAssessment = "slightly_fast";
      paceRecommendation = "Slightly fast. Take brief pauses between key points.";
    }
    
    const totalTrackingTime = timeAboveOptimal + timeBelowOptimal + timeInOptimal;
    
    const metrics = {
      audio_duration_seconds: duration,
      total_words: wordCount,
      speaking_pace_wpm: avgWPM,
      clarity_score: clarityScore,
      filler_word_count: fillerCount,
      filler_words_detected: fillerDetected,
      pace_assessment: paceAssessment,
      pace_recommendation: paceRecommendation,
      wpm_history: wpmHistory,
      time_above_optimal_seconds: timeAboveOptimal,
      time_below_optimal_seconds: timeBelowOptimal,
      time_in_optimal_seconds: timeInOptimal,
      optimal_percentage: totalTrackingTime > 0 ? Math.round((timeInOptimal / totalTrackingTime) * 100) : 0
    };
    
    setVoiceMetrics(metrics);
    setIsRecording(false);
    
    // Clear WPM interval
    if (wpmIntervalRef.current) {
      clearInterval(wpmIntervalRef.current);
    }
    
    toast.success(`Recording stopped. ${wordCount} words at ${avgWPM} WPM.`);
  };
  
  // Improved Video Setup
  const startVideo = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: "user"
        },
        audio: false 
      });
      
      streamRef.current = stream;
      
      // First enable video state so the video element renders
      setIsVideoEnabled(true);
      
      // Use setTimeout to ensure video element is in DOM before setting srcObject
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(e => {
              console.error("Video play error:", e);
            });
          };
        }
      }, 100);
      
      toast.success("📷 Camera enabled. Monitor your body language.");
    } catch (error) {
      console.error("Error accessing camera:", error);
      let errorMessage = "Failed to access camera.";
      
      if (error.name === "NotAllowedError") {
        errorMessage = "Camera access denied. Please allow camera permissions in your browser.";
      } else if (error.name === "NotFoundError") {
        errorMessage = "No camera found. Please connect a camera.";
      } else if (error.name === "NotReadableError") {
        errorMessage = "Camera is in use by another application.";
      }
      
      setCameraError(errorMessage);
      toast.error(errorMessage);
    }
  };
  
  const stopVideo = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsVideoEnabled(false);
    setCameraError(null);
  };
  
  // ==================== SESSION RECORDING FUNCTIONS ====================
  
  // Start recording the entire interview session
  const startSessionRecording = async () => {
    try {
      // Request both video and audio
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        audio: true
      });
      
      combinedStreamRef.current = videoStream;
      
      // Also enable video preview if not already enabled
      if (!isVideoEnabled) {
        streamRef.current = videoStream;
        setIsVideoEnabled(true);
        setTimeout(() => {
          if (videoRef.current && streamRef.current) {
            videoRef.current.srcObject = streamRef.current;
          }
        }, 100);
      }
      
      // Create MediaRecorder
      const options = { mimeType: 'video/webm;codecs=vp9,opus' };
      let mediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(videoStream, options);
      } catch (e) {
        // Fallback to default codec
        mediaRecorder = new MediaRecorder(videoStream);
      }
      
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          setRecordedChunks([...chunks]);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setSessionRecordingUrl(url);
        
        // Generate AI highlights based on session performance
        generateRecordingHighlights();
      };
      
      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setIsSessionRecording(true);
      setRecordingDuration(0);
      
      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
      toast.success("🎬 Session recording started! Your interview will be recorded for self-review.");
    } catch (error) {
      console.error("Error starting session recording:", error);
      let errorMsg = "Failed to start recording.";
      if (error.name === "NotAllowedError") {
        errorMsg = "Please allow camera and microphone access to record your session.";
      }
      toast.error(errorMsg);
    }
  };
  
  // Stop recording the session
  const stopSessionRecording = () => {
    if (mediaRecorderRef.current && isSessionRecording) {
      mediaRecorderRef.current.stop();
      setIsSessionRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      
      // Stop the combined stream tracks
      if (combinedStreamRef.current) {
        combinedStreamRef.current.getTracks().forEach(track => track.stop());
        combinedStreamRef.current = null;
      }
      
      toast.success("🎬 Recording stopped! You can now review your session.");
    }
  };
  
  // Generate AI highlights from the session
  const generateRecordingHighlights = () => {
    // Generate highlights based on session answers and feedback
    const highlights = [];
    
    if (session?.answers) {
      session.answers.forEach((ans, idx) => {
        const score = ans.ai_feedback?.overall_score || 0;
        const questionNum = idx + 1;
        
        if (score >= 80) {
          highlights.push({
            type: "best_answer",
            questionNumber: questionNum,
            timestamp: idx * 120, // Approximate timestamp (2 min per question)
            label: `🌟 Best Answer - Q${questionNum}`,
            description: ans.ai_feedback?.strengths?.[0] || "Excellent response!",
            score: score
          });
        } else if (score >= 60 && score < 70) {
          highlights.push({
            type: "improvement_area",
            questionNumber: questionNum,
            timestamp: idx * 120,
            label: `📝 Review Needed - Q${questionNum}`,
            description: ans.ai_feedback?.improvements?.[0] || "Room for improvement",
            score: score
          });
        }
      });
    }
    
    // Add summary highlight
    if (session?.overall_feedback) {
      highlights.unshift({
        type: "summary",
        timestamp: 0,
        label: "📊 Overall Performance",
        description: `Average Score: ${Math.round(session.overall_feedback.average_score || 0)}/100`,
        score: session.overall_feedback.average_score
      });
    }
    
    setRecordingHighlights(highlights);
  };
  
  // Download recording
  const downloadRecording = () => {
    if (sessionRecordingUrl) {
      const a = document.createElement('a');
      a.href = sessionRecordingUrl;
      a.download = `interview_session_${new Date().toISOString().slice(0, 10)}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("📥 Recording downloaded!");
    }
  };
  
  // Format recording duration
  const formatRecordingDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Get WPM status and color
  // getWPMStatus and getWPMMeterValue are now imported from components/interview
  
  // Start Interview
  const startInterview = async () => {
    if (!careerField.trim()) {
      toast.error("Please enter a career field");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/interview-buddy/start`,
        {
          mode: selectedMode,
          career_field: careerField,
          target_role: targetRole || careerField,
          company_culture: companyCulture,
          industry: industry,
          experience_level: experienceLevel,
          stress_level: stressLevel[0],
          panel_size: panelSize[0],
          adaptive_difficulty: adaptiveDifficulty
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSession(response.data.session);
      setCurrentQuestion(response.data.current_question);
      setCurrentInterviewer(response.data.current_interviewer);
      
      // Set timer for stress mode
      if (selectedMode === "stress") {
        setTimeRemaining(response.data.session.time_limit_per_question);
        setIsTimerRunning(true);
      }
      
      setStage("interview");
      toast.success(`${selectedMode.replace('_', ' ').toUpperCase()} interview started! Good luck!`);
    } catch (error) {
      console.error("Error starting interview:", error);
      toast.error("Failed to start interview. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  // Submit Answer
  const submitAnswer = async () => {
    if (!answer.trim()) {
      toast.error("Please provide an answer");
      return;
    }
    
    // Stop recording if still active
    if (isRecording) {
      stopRecording();
    }
    
    setIsTimerRunning(false);
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/interview-buddy/answer`,
        {
          session_id: session.id,
          question_index: session.current_question_index,
          answer_text: answer,
          voice_metrics: voiceMetrics,
          body_language: isVideoEnabled ? bodyLanguageAssessment : null,
          time_taken_seconds: session.time_limit_per_question - timeRemaining
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show feedback modal
      setCurrentFeedback(response.data);
      setShowFeedbackModal(true);
      
      if (response.data.completed) {
        // Interview complete
        setSession(prev => ({
          ...prev,
          overall_feedback: response.data.overall_feedback,
          benchmarks: response.data.benchmarks
        }));
        setBenchmarks(response.data.benchmarks || []);
      } else {
        // Move to next question
        setCurrentQuestion(response.data.next_question);
        setCurrentInterviewer(response.data.current_interviewer);
        setSession(prev => ({
          ...prev,
          current_question_index: prev.current_question_index + 1
        }));
        
        // Reset timer for stress mode
        if (selectedMode === "stress") {
          setTimeRemaining(session.time_limit_per_question);
        }
      }
      
      // Reset answer and metrics
      setAnswer("");
      setVoiceMetrics(null);
      setTranscript("");
      setLiveWPM(0);
      setWpmHistory([]);
      finalTranscriptRef.current = "";
      
    } catch (error) {
      console.error("Error submitting answer:", error);
      toast.error("Failed to submit answer. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  const closeFeedbackModal = () => {
    setShowFeedbackModal(false);
    
    if (currentFeedback?.completed) {
      setStage("feedback");
      fetchHistory();
      fetchBenchmarks();
    } else {
      // Start timer for next question in stress mode
      if (selectedMode === "stress") {
        setIsTimerRunning(true);
      }
    }
    setCurrentFeedback(null);
  };
  
  const resetInterview = () => {
    setStage("setup");
    setSession(null);
    setCurrentQuestion(null);
    setCurrentInterviewer(null);
    setAnswer("");
    setVoiceMetrics(null);
    setTranscript("");
    setTimeRemaining(0);
    setIsTimerRunning(false);
    setLiveWPM(0);
    setWpmHistory([]);
    finalTranscriptRef.current = "";
    stopVideo();
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };
  
  const viewSessionDetail = async (sessionData) => {
    try {
      const response = await axios.get(`${API}/interview-buddy/session/${sessionData.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSession(response.data);
      setStage("history-detail");
    } catch (error) {
      console.error("Error fetching session:", error);
      toast.error("Failed to load session details");
    }
  };
  
  // formatTime is now imported from components/interview
  
  const getModeIcon = (mode) => {
    const icons = {
      standard: <MessageSquare className="w-5 h-5" />,
      stress: <Timer className="w-5 h-5" />,
      panel: <Users className="w-5 h-5" />,
      technical: <Code className="w-5 h-5" />,
      salary_negotiation: <DollarSign className="w-5 h-5" />
    };
    return icons[mode] || <MessageSquare className="w-5 h-5" />;
  };
  
  const getTrendIcon = (trend) => {
    if (trend === "improving") return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === "declining") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  // WPMMeter is now imported from components/interview

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              AI Interview Buddy
            </h1>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Practice interviews with adaptive AI that analyzes your voice, provides real-time feedback, and helps you become interview-ready.
          </p>
        </div>

        {/* Setup Stage */}
        {stage === "setup" && (
          <div className="max-w-4xl mx-auto">
            {/* Resume Previous Interview Card - only show if user has paused/in-progress sessions */}
            <ResumableInterviewsCard
              sessions={resumableSessions}
              onResume={handleResumeInterview}
              loading={loading}
            />
            
            <Tabs defaultValue="configure" className="w-full">
              <TabsList className="flex w-full mb-6 overflow-x-auto" data-testid="interview-tabs">
                <TabsTrigger value="configure" className="flex-1 min-w-0 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">Configure Interview</TabsTrigger>
                <TabsTrigger value="history" className="flex-1 min-w-0 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">Past Interviews</TabsTrigger>
                <TabsTrigger value="benchmarks" className="flex-1 min-w-0 text-xs sm:text-sm whitespace-nowrap px-2 sm:px-4">My Benchmarks</TabsTrigger>
              </TabsList>
              
              {/* Configure Tab */}
              <TabsContent value="configure">
                <Card className="border-0 shadow-xl bg-white/80 backdrop-blur">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      Start Your AI Interview
                    </CardTitle>
                    <CardDescription>
                      Choose your interview mode and customize the experience
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Interview Mode Selection */}
                    <div>
                      <label className="block text-sm font-medium mb-3">Interview Mode</label>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {config?.modes?.map((mode) => (
                          <button
                            key={mode.id}
                            onClick={() => setSelectedMode(mode.id)}
                            className={`p-4 rounded-xl border-2 transition-all text-center ${
                              selectedMode === mode.id
                                ? 'border-purple-500 bg-purple-50 shadow-md'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                            }`}
                          >
                            <div className="text-2xl mb-1">{mode.icon}</div>
                            <p className="font-medium text-sm">{mode.name}</p>
                          </button>
                        ))}
                      </div>
                      {config?.modes?.find(m => m.id === selectedMode) && (
                        <p className="text-sm text-gray-600 mt-2 p-3 bg-gray-50 rounded-lg">
                          {config.modes.find(m => m.id === selectedMode).description}
                        </p>
                      )}
                    </div>
                    
                    {/* Basic Configuration */}
                    
                    {/* Resume-Based Interview Toggle */}
                    <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border-2 border-emerald-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <FileText className="w-6 h-6 text-emerald-600" />
                          <div>
                            <p className="font-semibold text-emerald-800">Resume-Based Interview</p>
                            <p className="text-sm text-emerald-600">Get personalized questions based on your resume</p>
                          </div>
                        </div>
                        <Switch
                          checked={useResume}
                          onCheckedChange={setUseResume}
                          data-testid="resume-toggle"
                        />
                      </div>
                      
                      {useResume && (
                        <div className="mt-4 space-y-4">
                          {/* Saved Resume Info */}
                          {savedResume && !resumeFile && (
                            <div className="p-3 bg-white rounded-lg border border-emerald-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <FileCheck className="w-5 h-5 text-emerald-600" />
                                  <div>
                                    <p className="font-medium text-emerald-800">Previously Parsed Resume</p>
                                    <p className="text-sm text-gray-500">{savedResume.filename || 'Resume'}</p>
                                  </div>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setSavedResume(null);
                                    setParsedResume(null);
                                  }}
                                >
                                  Upload New
                                </Button>
                              </div>
                            </div>
                          )}
                          
                          {/* Resume Upload */}
                          {(!savedResume || resumeFile) && (
                            <div className="space-y-3">
                              <div 
                                className="border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors cursor-pointer bg-white"
                                onClick={() => resumeInputRef.current?.click()}
                              >
                                <input
                                  type="file"
                                  ref={resumeInputRef}
                                  onChange={handleResumeFileChange}
                                  accept=".pdf,.docx,.txt"
                                  className="hidden"
                                  data-testid="resume-file-input"
                                />
                                {resumeFile ? (
                                  <div className="flex items-center justify-center gap-3">
                                    <FileText className="w-8 h-8 text-emerald-600" />
                                    <div className="text-left">
                                      <p className="font-medium text-emerald-800">{resumeFile.name}</p>
                                      <p className="text-sm text-gray-500">{(resumeFile.size / 1024).toFixed(1)} KB</p>
                                    </div>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setResumeFile(null);
                                        setParsedResume(null);
                                      }}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <>
                                    <Upload className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                                    <p className="text-emerald-700 font-medium">Click to upload your resume</p>
                                    <p className="text-sm text-gray-500 mt-1">PDF, DOCX, or TXT (max 5MB)</p>
                                  </>
                                )}
                              </div>
                              
                              {resumeFile && !parsedResume && (
                                <div className="flex items-center gap-4">
                                  {/* AI Parsing Toggle */}
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={useAIParsing}
                                      onCheckedChange={setUseAIParsing}
                                      data-testid="ai-parsing-toggle"
                                    />
                                    <Label className="text-sm cursor-pointer flex items-center gap-1">
                                      <Wand2 className="w-4 h-4 text-purple-500" />
                                      AI-Powered Parsing
                                      <span className="text-xs text-gray-400">(more accurate)</span>
                                    </Label>
                                  </div>
                                  
                                  <Button
                                    onClick={parseResume}
                                    disabled={resumeParsing}
                                    className="ml-auto bg-emerald-600 hover:bg-emerald-700"
                                    data-testid="parse-resume-btn"
                                  >
                                    {resumeParsing ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Parsing...
                                      </>
                                    ) : (
                                      <>
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        Parse Resume
                                      </>
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Parsed Resume Preview */}
                          {parsedResume && (
                            <div className="p-4 bg-white rounded-lg border border-emerald-200 space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="font-semibold text-emerald-800 flex items-center gap-2">
                                  <FileCheck className="w-5 h-5" />
                                  Resume Parsed Successfully!
                                </h4>
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  {parsedResume.all_skills?.length || parsedResume.skills?.length || 0} skills found
                                </Badge>
                              </div>
                              
                              {parsedResume.name && (
                                <p className="text-gray-700"><strong>Name:</strong> {parsedResume.name}</p>
                              )}
                              
                              {parsedResume.total_experience && (
                                <p className="text-gray-700"><strong>Experience:</strong> {parsedResume.total_experience}</p>
                              )}
                              
                              {/* Skills Preview */}
                              {(parsedResume.all_skills || parsedResume.skills) && (
                                <div>
                                  <p className="text-sm font-medium text-gray-600 mb-2">Top Skills:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {(parsedResume.all_skills || 
                                      (typeof parsedResume.skills === 'object' ? [...(parsedResume.skills.technical || []), ...(parsedResume.skills.tools || [])] : parsedResume.skills) || 
                                      []).slice(0, 10).map((skill, idx) => (
                                      <Badge key={idx} variant="outline" className="bg-emerald-50">
                                        {skill}
                                      </Badge>
                                    ))}
                                    {((parsedResume.all_skills || parsedResume.skills || []).length > 10) && (
                                      <Badge variant="outline" className="bg-gray-50">
                                        +{(parsedResume.all_skills || parsedResume.skills).length - 10} more
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              <div className="pt-2 border-t">
                                <p className="text-sm text-emerald-600 flex items-center gap-1">
                                  <Sparkles className="w-4 h-4" />
                                  Interview questions will be personalized based on your skills and experience!
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Career Field *</label>
                        <input
                          type="text"
                          value={careerField}
                          onChange={(e) => setCareerField(e.target.value)}
                          placeholder="e.g., Software Engineer, Data Scientist"
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Target Role</label>
                        <input
                          type="text"
                          value={targetRole}
                          onChange={(e) => setTargetRole(e.target.value)}
                          placeholder="e.g., Senior Frontend Developer"
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Company Culture</label>
                        <Select value={companyCulture} onValueChange={setCompanyCulture}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {config?.company_cultures?.map((culture) => (
                              <SelectItem key={culture.id} value={culture.id}>
                                {culture.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Industry</label>
                        <Select value={industry} onValueChange={setIndustry}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {config?.industries?.map((ind) => (
                              <SelectItem key={ind.id} value={ind.id}>
                                {ind.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Experience Level</label>
                        <Select value={experienceLevel} onValueChange={setExperienceLevel}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {config?.experience_levels?.map((level) => (
                              <SelectItem key={level.id} value={level.id}>
                                {level.name} ({level.years})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Mode-specific options */}
                    {selectedMode === "stress" && (
                      <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Timer className="w-5 h-5 text-orange-600" />
                          <span className="font-medium text-orange-800">Stress Level</span>
                        </div>
                        <Slider
                          value={stressLevel}
                          onValueChange={setStressLevel}
                          min={1}
                          max={5}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-sm text-orange-700 mt-2">
                          <span>Mild Pressure</span>
                          <span>Level {stressLevel[0]}</span>
                          <span>Intense</span>
                        </div>
                      </div>
                    )}
                    
                    {selectedMode === "panel" && (
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-2 mb-3">
                          <Users className="w-5 h-5 text-blue-600" />
                          <span className="font-medium text-blue-800">Panel Size</span>
                        </div>
                        <Slider
                          value={panelSize}
                          onValueChange={setPanelSize}
                          min={2}
                          max={5}
                          step={1}
                          className="w-full"
                        />
                        <p className="text-sm text-blue-700 mt-2">
                          {panelSize[0]} interviewers with different personas will question you
                        </p>
                      </div>
                    )}
                    
                    {/* Adaptive Difficulty Toggle */}
                    <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200">
                      <div className="flex items-center gap-3">
                        <Brain className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-medium text-purple-800">Adaptive Difficulty</p>
                          <p className="text-sm text-purple-600">Questions adjust based on your performance</p>
                        </div>
                      </div>
                      <Switch
                        checked={adaptiveDifficulty}
                        onCheckedChange={setAdaptiveDifficulty}
                      />
                    </div>
                    
                    {/* AI Analysis Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-xl border border-blue-200">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-blue-800">AI Body Language Analysis</p>
                          <p className="text-sm text-blue-600">Real-time CV + facial expression + voice analysis</p>
                        </div>
                      </div>
                      <Switch
                        checked={mlAnalysisEnabled}
                        onCheckedChange={setMlAnalysisEnabled}
                      />
                    </div>
                    
                    <Button
                      onClick={useResume && parsedResume ? startResumeInterview : startInterview}
                      disabled={loading || (!useResume && !careerField.trim()) || (useResume && !parsedResume)}
                      className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white py-6 text-lg"
                      data-testid="start-interview-btn"
                    >
                      {loading ? (
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      ) : useResume && parsedResume ? (
                        <FileText className="w-5 h-5 mr-2" />
                      ) : (
                        getModeIcon(selectedMode)
                      )}
                      <span className="ml-2">
                        {loading ? "Preparing Interview..." : 
                          useResume && parsedResume ? 
                          `Start Resume-Based ${selectedMode.replace('_', ' ').toUpperCase()} Interview` :
                          `Start ${selectedMode.replace('_', ' ').toUpperCase()} Interview`
                        }
                      </span>
                    </Button>
                    
                    {useResume && !parsedResume && (
                      <p className="text-center text-sm text-amber-600 flex items-center justify-center gap-1">
                        <AlertCircleIcon className="w-4 h-4" />
                        Please upload and parse your resume first to start a resume-based interview
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* History Tab */}
              <TabsContent value="history">
                <Card className="border-0 shadow-xl bg-white/80 backdrop-blur">
                  <CardHeader>
                    <CardTitle>Interview History</CardTitle>
                    <CardDescription>Review your past interview sessions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {history.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No interviews yet. Start your first one!</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {history.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${
                                item.mode === 'stress' ? 'bg-orange-100' :
                                item.mode === 'panel' ? 'bg-blue-100' :
                                item.mode === 'technical' ? 'bg-green-100' :
                                item.mode === 'salary_negotiation' ? 'bg-yellow-100' :
                                'bg-purple-100'
                              }`}>
                                {getModeIcon(item.mode)}
                              </div>
                              <div>
                                <p className="font-medium">{item.target_role || item.career_field}</p>
                                <p className="text-sm text-gray-600">
                                  {item.mode.replace('_', ' ')} • {item.industry} • {new Date(item.started_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="flex items-center gap-1">
                                  <span className="font-bold text-lg">{Math.round(item.running_score)}</span>
                                  <span className="text-gray-500">/100</span>
                                  {getTrendIcon(item.performance_trend)}
                                </div>
                                <Badge variant={item.completed ? "default" : "secondary"}>
                                  {item.completed ? "Completed" : `${item.questions_answered}/${item.total_questions}`}
                                </Badge>
                              </div>
                              {item.completed && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewSessionDetail(item)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  Review
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Benchmarks Tab */}
              <TabsContent value="benchmarks">
                <Card className="border-0 shadow-xl bg-white/80 backdrop-blur">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-blue-500" />
                      Performance Benchmarks
                    </CardTitle>
                    <CardDescription>Compare your performance with other candidates</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {benchmarks.length === 0 ? (
                      <div className="text-center py-12">
                        <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Complete interviews to see your benchmarks</p>
                        <Button onClick={fetchBenchmarks} variant="outline" className="mt-4">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh Benchmarks
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {benchmarks.map((benchmark, idx) => (
                          <div key={idx} className="p-4 bg-gray-50 rounded-xl">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-medium">{benchmark.metric_name}</h4>
                              <div className="flex items-center gap-2">
                                <Badge variant={benchmark.your_average >= benchmark.average_benchmark ? "default" : "secondary"}>
                                  Your Avg: {benchmark.your_average}
                                </Badge>
                                {benchmark.trend === "improving" && (
                                  <TrendingUp className="w-4 h-4 text-green-500" />
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 w-24">You</span>
                                <Progress value={benchmark.your_average} className="flex-1 h-3" />
                                <span className="text-sm font-medium w-12">{benchmark.your_average}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 w-24">Average</span>
                                <Progress value={benchmark.average_benchmark} className="flex-1 h-3 bg-gray-300" />
                                <span className="text-sm text-gray-600 w-12">{benchmark.average_benchmark}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 w-24">Top 10%</span>
                                <Progress value={benchmark.top_10_percent} className="flex-1 h-3 bg-green-200" />
                                <span className="text-sm text-green-600 w-12">{benchmark.top_10_percent}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        )}

        {/* Interview Stage */}
        {stage === "interview" && currentQuestion && session && (
          <div className="max-w-5xl mx-auto">
            {/* Progress and Controls */}
            <Card className="mb-6 border-0 shadow-lg bg-white/80 backdrop-blur">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="text-lg px-4 py-1">
                      Q{session.current_question_index + 1}/{session.total_questions}
                    </Badge>
                    <Badge className={`${
                      currentQuestion.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                      currentQuestion.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      currentQuestion.difficulty === 'hard' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {currentQuestion.difficulty?.toUpperCase()}
                    </Badge>
                    {session.mode === "panel" && currentInterviewer && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
                        <span className="text-xl">{currentInterviewer.avatar_emoji}</span>
                        <span className="text-sm font-medium text-blue-800">{currentInterviewer.name}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Timer for Stress Mode */}
                  {session.mode === "stress" && (
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                      timeRemaining <= 30 ? 'bg-red-100 text-red-700 animate-pulse' :
                      timeRemaining <= 60 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      <Timer className="w-5 h-5" />
                      <span className="font-mono text-lg font-bold">{formatTime(timeRemaining)}</span>
                    </div>
                  )}
                </div>
                
                <Progress 
                  value={((session.current_question_index + 1) / session.total_questions) * 100} 
                  className="h-2"
                />
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Question and Answer Area */}
              <div className="lg:col-span-2 space-y-6">
                {/* Question Card */}
                <Card className="border-0 shadow-lg bg-white/80 backdrop-blur">
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Target className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl leading-relaxed">
                          {currentQuestion.question}
                        </CardTitle>
                        {currentQuestion.category && (
                          <Badge variant="secondary" className="mt-2">
                            {currentQuestion.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {currentQuestion.key_points_to_cover?.length > 0 && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-800 mb-2">💡 Key points to cover:</p>
                        <div className="flex flex-wrap gap-2">
                          {currentQuestion.key_points_to_cover.map((point, i) => (
                            <Badge key={i} variant="outline" className="text-blue-700">
                              {point}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Voice Controls */}
                    <div className="flex items-center gap-3 mb-4">
                      <Button
                        variant={isRecording ? "destructive" : "outline"}
                        onClick={isRecording ? stopRecording : startRecording}
                        className="gap-2"
                      >
                        {isRecording ? (
                          <>
                            <MicOff className="w-4 h-4" />
                            Stop Recording
                          </>
                        ) : (
                          <>
                            <Mic className="w-4 h-4" />
                            Start Voice Answer
                          </>
                        )}
                      </Button>
                      
                      {isRecording && (
                        <div className="flex items-center gap-2 text-red-600">
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <span className="text-sm font-medium">Recording...</span>
                        </div>
                      )}
                      
                      {voiceMetrics && (
                        <Badge variant="secondary" className="gap-1">
                          <Volume2 className="w-3 h-3" />
                          {voiceMetrics.speaking_pace_wpm} WPM avg
                        </Badge>
                      )}
                    </div>
                    
                    {/* Live transcript preview when recording */}
                    {isRecording && (
                      <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                          <p className="text-sm font-medium text-purple-800">Live Transcript</p>
                          <span className="text-xs text-purple-600">(speaking...)</span>
                        </div>
                        <div className="text-gray-700 text-sm whitespace-pre-wrap min-h-[40px] max-h-[120px] overflow-y-auto">
                          {transcript ? (
                            <>
                              {/* Final text (confirmed) */}
                              <span className="text-gray-800">{finalTranscriptRef.current}</span>
                              {/* Interim text (being recognized) - shown differently */}
                              {transcript.length > finalTranscriptRef.current.length && (
                                <span className="text-purple-600 italic bg-purple-100 px-1 rounded">
                                  {transcript.slice(finalTranscriptRef.current.length)}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400 italic">Start speaking...</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <Textarea
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                      placeholder={isRecording ? "Your speech will appear here as you speak..." : "Type your answer or use voice recording..."}
                      rows={8}
                      className="mb-4 text-base"
                    />
                    
                    {/* Word count */}
                    <div className="flex justify-between text-sm text-gray-500 mb-4">
                      <span>{answer.split(/\s+/).filter(w => w.length > 0).length} words</span>
                      {voiceMetrics && (
                        <span className="flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full ${
                            voiceMetrics.clarity_score >= 80 ? 'bg-green-500' :
                            voiceMetrics.clarity_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }`} />
                          Clarity: {voiceMetrics.clarity_score}%
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={submitAnswer}
                        disabled={loading || !answer.trim()}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        data-testid="submit-answer-btn"
                      >
                        {loading ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        {loading ? "Analyzing..." : "Submit Answer"}
                      </Button>
                      <Button
                        onClick={handlePauseInterview}
                        disabled={pausing || loading}
                        variant="outline"
                        className="sm:w-auto border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                        data-testid="pause-interview-btn"
                      >
                        {pausing ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Pause className="w-4 h-4 mr-2" />
                        )}
                        Save & Continue Later
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar - Video, WPM Meter, and Self Assessment */}
              <div className="space-y-4">
                {/* Real-time WPM Meter */}
                <WPMMeter wpm={liveWPM} isActive={isRecording} />
                
                {/* Video Preview */}
                <Card className="border-0 shadow-lg bg-white/80 backdrop-blur">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Body Language Monitor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden mb-3 relative">
                      {isVideoEnabled ? (
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          className="w-full h-full object-cover transform scale-x-[-1]"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                          <VideoOff className="w-8 h-8 mb-2" />
                          <span className="text-xs">Camera Off</span>
                          {cameraError && (
                            <p className="text-xs text-red-400 mt-2 px-4 text-center">{cameraError}</p>
                          )}
                        </div>
                      )}
                      {isVideoEnabled && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-green-500 rounded-full">
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          <span className="text-xs text-white font-medium">LIVE</span>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={isVideoEnabled ? stopVideo : startVideo}
                      className="w-full"
                    >
                      {isVideoEnabled ? (
                        <>
                          <VideoOff className="w-4 h-4 mr-2" />
                          Disable Camera
                        </>
                      ) : (
                        <>
                          <Video className="w-4 h-4 mr-2" />
                          Enable Camera
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Session Recording Card */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-pink-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Film className="w-4 h-4 text-red-500" />
                      Session Recording
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {isSessionRecording && (
                        <div className="flex items-center justify-between p-2 bg-red-100 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                            <span className="text-sm font-medium text-red-700">Recording</span>
                          </div>
                          <span className="text-sm font-mono text-red-700">
                            {formatRecordingDuration(recordingDuration)}
                          </span>
                        </div>
                      )}
                      
                      <Button
                        variant={isSessionRecording ? "destructive" : "outline"}
                        size="sm"
                        onClick={isSessionRecording ? stopSessionRecording : startSessionRecording}
                        className="w-full"
                      >
                        {isSessionRecording ? (
                          <>
                            <StopCircle className="w-4 h-4 mr-2" />
                            Stop Recording
                          </>
                        ) : (
                          <>
                            <Circle className="w-4 h-4 mr-2 text-red-500" />
                            Record Session
                          </>
                        )}
                      </Button>
                      
                      {sessionRecordingUrl && !isSessionRecording && (
                        <div className="space-y-2 pt-2 border-t">
                          <p className="text-xs text-gray-600">Recording saved! Review your performance:</p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowRecordingPreview(true)}
                              className="flex-1"
                            >
                              <PlayCircle className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={downloadRecording}
                              className="flex-1"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 mt-2">
                        📹 Record your interview for AI-powered highlight reel and self-review
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Self Assessment */}
                {isVideoEnabled && mlAnalysisEnabled && (
                  <MLAnalysisPanel
                    videoRef={videoRef}
                    isVideoEnabled={isVideoEnabled}
                    voiceMetrics={voiceMetrics}
                    isRecording={isRecording}
                    onBodyLanguageUpdate={setBodyLanguageAnalysis}
                    onExpressionUpdate={setFacialExpressions}
                  />
                )}

                {/* Manual Self Assessment (Fallback) */}
                {isVideoEnabled && !mlAnalysisEnabled && (
                  <Card className="border-0 shadow-lg bg-white/80 backdrop-blur">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Self-Assessment</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {[
                        { key: "eye_contact", label: "Eye Contact" },
                        { key: "posture", label: "Posture" },
                        { key: "facial_expressions", label: "Expressions" },
                        { key: "overall_presence", label: "Presence" }
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <div className="flex justify-between text-sm mb-1">
                            <span>{label}</span>
                            <span className="font-medium">{bodyLanguageAssessment[key]}/10</span>
                          </div>
                          <Slider
                            value={[bodyLanguageAssessment[key]]}
                            onValueChange={([val]) => setBodyLanguageAssessment(prev => ({
                              ...prev,
                              [key]: val
                            }))}
                            min={1}
                            max={10}
                            step={1}
                            className="w-full"
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Quick Tips */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-pink-50">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-purple-800">Quick Tips</span>
                    </div>
                    <ul className="text-sm text-purple-700 space-y-1">
                      <li>• Use STAR method for behavioral questions</li>
                      <li>• <span className="font-semibold">Speak at 120-150 WPM</span> for clarity</li>
                      <li>• Avoid filler words (um, like, you know)</li>
                      <li>• Maintain eye contact with camera</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Feedback Stage */}
        {stage === "feedback" && session && (
          <InterviewFeedbackView
            session={session}
            benchmarks={benchmarks}
            onReset={resetInterview}
            onViewHistory={() => { setStage("setup"); fetchHistory(); fetchResumableSessions(); }}
          />
        )}

        {/* History Detail View */}
        {stage === "history-detail" && selectedSession && (
          <InterviewHistoryDetail
            session={selectedSession}
            onBack={() => { setStage("setup"); setSelectedSession(null); }}
          />
        )}

        {/* Feedback Modal */}
        <Dialog open={showFeedbackModal} onOpenChange={closeFeedbackModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Answer Feedback
              </DialogTitle>
              <DialogDescription>AI analysis of your response</DialogDescription>
            </DialogHeader>
            
            {currentFeedback && (
              <div className="space-y-4">
                {/* Score Overview */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{currentFeedback.feedback?.grammar_score}/10</p>
                    <p className="text-xs text-gray-600">Grammar</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{currentFeedback.feedback?.quality_score}/10</p>
                    <p className="text-xs text-gray-600">Quality</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{currentFeedback.feedback?.relevance_score}/10</p>
                    <p className="text-xs text-gray-600">Relevance</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-2xl font-bold text-orange-600">{currentFeedback.current_score}</p>
                    <p className="text-xs text-gray-600">Overall</p>
                  </div>
                </div>

                {/* Voice Analysis with WPM details */}
                {currentFeedback.voice_analysis && Object.keys(currentFeedback.voice_analysis).length > 0 && (
                  <div className="p-4 bg-indigo-50 rounded-lg">
                    <p className="font-medium text-indigo-900 mb-3 flex items-center gap-2">
                      <Volume2 className="w-4 h-4" />
                      Voice Analysis
                    </p>
                    <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-indigo-600 font-bold text-lg">{currentFeedback.voice_analysis.speaking_pace_wpm || 0}</p>
                        <p className="text-gray-600 text-xs">WPM Average</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-indigo-600 font-bold text-lg">{currentFeedback.voice_analysis.filler_word_count || 0}</p>
                        <p className="text-gray-600 text-xs">Filler Words</p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg">
                        <p className="text-indigo-600 font-bold text-lg">{currentFeedback.voice_analysis.clarity_score || 0}%</p>
                        <p className="text-gray-600 text-xs">Clarity</p>
                      </div>
                    </div>
                    
                    {/* WPM Zone Analysis */}
                    {currentFeedback.voice_analysis.optimal_percentage !== undefined && (
                      <div className="mt-3 p-3 bg-white rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-2">Speaking Pace Breakdown</p>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden flex">
                            <div 
                              className="bg-red-400 h-full" 
                              style={{ width: `${100 - currentFeedback.voice_analysis.optimal_percentage}%` }}
                            />
                            <div 
                              className="bg-green-500 h-full" 
                              style={{ width: `${currentFeedback.voice_analysis.optimal_percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-green-600">
                            {currentFeedback.voice_analysis.optimal_percentage}% optimal
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          Time in optimal range (120-150 WPM): {currentFeedback.voice_analysis.time_in_optimal_seconds || 0}s
                        </p>
                      </div>
                    )}
                    
                    {currentFeedback.voice_analysis.pace_recommendation && (
                      <p className="text-sm text-indigo-700 mt-2 p-2 bg-white rounded">
                        💡 {currentFeedback.voice_analysis.pace_recommendation}
                      </p>
                    )}
                  </div>
                )}

                {/* Performance Indicators */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Badge variant={currentFeedback.feedback?.confidence_assessment === "high" ? "default" : "secondary"}>
                      {currentFeedback.feedback?.confidence_assessment?.toUpperCase()} Confidence
                    </Badge>
                    {getTrendIcon(currentFeedback.performance_trend)}
                    <span className="text-sm text-gray-600">{currentFeedback.performance_trend}</span>
                  </div>
                  <Badge variant="outline">
                    Difficulty: {currentFeedback.difficulty_level}
                  </Badge>
                </div>

                {/* Strengths */}
                {currentFeedback.feedback?.strengths?.length > 0 && (
                  <div>
                    <p className="font-medium text-green-700 mb-2 flex items-center gap-1">
                      <ThumbsUp className="w-4 h-4" />
                      Strengths
                    </p>
                    <ul className="space-y-1">
                      {currentFeedback.feedback.strengths.map((s, i) => (
                        <li key={i} className="text-gray-700 flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Improvements */}
                {currentFeedback.feedback?.improvements?.length > 0 && (
                  <div>
                    <p className="font-medium text-orange-700 mb-2 flex items-center gap-1">
                      <ThumbsDown className="w-4 h-4" />
                      Areas for Improvement
                    </p>
                    <ul className="space-y-1">
                      {currentFeedback.feedback.improvements.map((s, i) => (
                        <li key={i} className="text-gray-700 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tips */}
                {currentFeedback.feedback?.tips && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-900 mb-1 flex items-center gap-1">
                      <Lightbulb className="w-4 h-4" />
                      Pro Tip
                    </p>
                    <p className="text-gray-700">{currentFeedback.feedback.tips}</p>
                  </div>
                )}

                {/* Running Average */}
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="text-purple-800">Running Average</span>
                  <span className="text-2xl font-bold text-purple-600">
                    {Math.round(currentFeedback.running_average)}/100
                  </span>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button onClick={closeFeedbackModal} className="w-full">
                {currentFeedback?.completed ? "View Final Results" : "Continue to Next Question"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Recording Preview Modal */}
        <Dialog open={showRecordingPreview} onOpenChange={setShowRecordingPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Film className="w-5 h-5 text-red-500" />
                Interview Recording Review
              </DialogTitle>
              <DialogDescription>
                Review your recorded session with AI-generated highlights
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Video Player */}
              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                {sessionRecordingUrl && (
                  <video
                    src={sessionRecordingUrl}
                    controls
                    className="w-full h-full"
                  />
                )}
              </div>
              
              {/* AI Highlights */}
              {recordingHighlights.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    AI-Generated Highlights
                  </h4>
                  <div className="grid gap-2">
                    {recordingHighlights.map((highlight, idx) => (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${
                          highlight.type === "best_answer" 
                            ? "bg-green-50 border-green-200" 
                            : highlight.type === "improvement_area"
                            ? "bg-orange-50 border-orange-200"
                            : "bg-blue-50 border-blue-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{highlight.label}</span>
                          {highlight.score && (
                            <Badge variant={highlight.score >= 80 ? "default" : "secondary"}>
                              Score: {Math.round(highlight.score)}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{highlight.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={downloadRecording}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Recording
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowRecordingPreview(false)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default AIInterviewBuddy;
